<template>
  <v-dialog
    data-testid="roleAssignmentModal-dialog"
    max-width="560"
    :value="show"
    @click:outside="handleCancel"
    @keydown.esc="handleCancel"
  >
    <v-card class="px-6 py-4">
      <v-card-title class="ma-0 pa-0 mb-6">{{
        $t('common.admin_role_one')
      }}</v-card-title>
      <v-card-text class="ma-0 pa-0">
        <v-radio-group
          v-model="selectedRole"
          hide-details
          class="ma-0 pa-0"
          data-testid="roleAssignmentModal-radioGroup-role"
        >
          <v-radio
            v-for="role in adminRoles"
            :key="role.id"
            :data-testid="`roleAssignmentModal-radio-${role.id}`"
            :ripple="false"
            :value="role.id"
          >
            <template v-slot:label>
              <span class="mx-0 px-0 font-size-13 font-weight-500">
                {{ role.text }}
              </span>
            </template>
          </v-radio>
        </v-radio-group>

        <div
          v-if="selectedRole === ADMIN_ROLES.SITE_ADMIN"
          class="mt-4"
          data-testid="roleAssignmentModal-section-siteScope"
        >
          <div
            v-if="defaultSiteName"
            class="mb-4"
            data-testid="roleAssignmentModal-section-defaultSite"
          >
            <p
              class="text-caption blue-grey--text text--darken-1 ma-0 pa-0 mb-1"
            >
              {{ $t('organization_users.default_site') }}
            </p>
            <v-chip
              small
              outlined
              disabled
              class="mt-1"
              data-testid="roleAssignmentModal-chip-defaultSite"
            >
              {{ defaultSiteName }}
            </v-chip>
          </div>

          <p
            class="text-body-2 font-weight-medium blue-grey--text text--darken-2 ma-0 pa-0 mb-1"
          >
            {{ $t('organization_users.optional') }}
          </p>
          <p class="text-caption blue-grey--text text--darken-1 ma-0 pa-0 mb-3">
            {{ $t('organization_users.additional_scope_description') }}
          </p>

          <MultipleSiteSelector
            data-testid="roleAssignmentModal-siteSelector-managedSites"
            :options="assignableSites"
            :value="selectedSiteIds"
            @change="selectedSiteIds = $event"
          />

          <p
            class="text-caption blue-grey--text text--darken-1 ma-0 pa-0 mt-3 mb-1"
          >
            {{ $t('common.site_group_other') }}
          </p>
          <MultipleSiteGroupSelector
            data-testid="roleAssignmentModal-siteGroupSelector-managedSiteGroups"
            :options="assignableSiteGroups"
            :value="selectedSiteGroupIds"
            @change="selectedSiteGroupIds = $event"
          />

          <div
            v-if="hasSitesIncludedFromGroups"
            class="mt-3"
            data-testid="roleAssignmentModal-section-sitesIncludedFromGroups"
          >
            <p
              class="text-caption blue-grey--text text--darken-1 ma-0 pa-0 mb-1"
            >
              {{ $t('organization_users.included_from_site_groups') }}
            </p>
            <v-chip
              v-for="site in sitesIncludedFromGroups"
              :key="site.id"
              small
              outlined
              disabled
              class="me-2 mt-1"
              data-testid="roleAssignmentModal-chip-siteIncludedFromGroup"
            >
              {{ site.name }}
            </v-chip>
          </div>
        </div>
      </v-card-text>
      <v-card-actions class="align-center justify-end ma-0 pa-0 mt-4">
        <Button
          plain
          color="grey"
          data-testid="roleAssignmentModal-button-cancel"
          :disabled="isSaving"
          :text="$t('common.button.cancel')"
          @click="handleCancel"
        />
        <Button
          color="primary"
          data-testid="roleAssignmentModal-button-save"
          :loading="isSaving"
          :text="$t('common.button.save')"
          @click="handleSave"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import { utils } from 'frontend-js-shared'

import { ADMIN_ROLES } from '@/constants'

import Button from '@/components/UI/Button'
import MultipleSiteSelector from '@/components/MultipleSiteSelector.vue'
import MultipleSiteGroupSelector from '@/components/MultipleSiteGroupSelector.vue'
import adminRoleMixin from '@/mixins/adminRoleMixin'

export default {
  name: 'RoleAssignmentModal',

  mixins: [adminRoleMixin],

  components: {
    Button,
    MultipleSiteSelector,
    MultipleSiteGroupSelector,
  },

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    user: {
      type: Object,
      required: true,
    },
    sites: {
      type: Array,
      default: null,
    },
    siteGroups: {
      type: Array,
      default: null,
    },
  },

  data() {
    return {
      selectedRole: ADMIN_ROLES.NONE,
      selectedSiteIds: [],
      selectedSiteGroupIds: [],
      isSaving: false,
    }
  },

  computed: {
    ...mapGetters([
      'account',
      'authorizedSiteIds',
      'organizationSiteGroups',
      'organizationSites',
    ]),

    isOperatorOrgAdmin() {
      return Boolean(this.account?.isOrganizationAdmin || this.account?.isAdmin)
    },

    resolvedSites() {
      return this.sites ?? this.organizationSites
    },

    resolvedSiteGroups() {
      return this.siteGroups ?? this.organizationSiteGroups
    },

    defaultSiteName() {
      return this.user?.site?.name ?? null
    },

    assignableSites() {
      const defaultSiteId = utils.getEntityId(this.user?.site)
      const authorizedIds = new Set(this.authorizedSiteIds ?? [])

      const scopeSites = this.isOperatorOrgAdmin
        ? this.resolvedSites
        : this.resolvedSites.filter(site =>
            authorizedIds.has(utils.getEntityId(site))
          )

      return scopeSites.filter(
        site => utils.getEntityId(site) !== defaultSiteId
      )
    },

    assignableSiteGroups() {
      if (this.isOperatorOrgAdmin) return this.resolvedSiteGroups

      const managedSiteGroupIds = new Set(
        utils.getManagedSiteGroups(this.account)
      )
      return this.resolvedSiteGroups.filter(siteGroup =>
        managedSiteGroupIds.has(utils.getEntityId(siteGroup))
      )
    },

    sitesIncludedFromGroups() {
      const selectedSiteGroupIds = new Set(this.selectedSiteGroupIds)
      const explicitSiteIds = new Set(this.selectedSiteIds)
      const defaultSiteId = utils.getEntityId(this.user?.site)

      const sites = this.resolvedSiteGroups
        .filter(siteGroup =>
          selectedSiteGroupIds.has(utils.getEntityId(siteGroup))
        )
        .flatMap(siteGroup => siteGroup.sites ?? [])
        .map(site => ({ id: utils.getEntityId(site), name: site.name }))
        .filter(
          site =>
            site.id &&
            !explicitSiteIds.has(site.id) &&
            site.id !== defaultSiteId
        )

      return _.uniqBy(sites, 'id')
    },

    hasSitesIncludedFromGroups() {
      return !_.isEmpty(this.sitesIncludedFromGroups)
    },
  },

  watch: {
    show: {
      handler(open) {
        if (open) this.initializeFromUser()
      },
      immediate: true,
    },
  },

  methods: {
    ...mapActions(['updateAccount']),

    initializeFromUser() {
      this.selectedSiteIds = utils.getManagedSites(this.user)
      this.selectedSiteGroupIds = utils.getManagedSiteGroups(this.user)

      if (this.user?.isOrganizationAdmin) {
        this.selectedRole = ADMIN_ROLES.ORGANIZATION_ADMIN
        return
      }

      if (this.user?.isSiteAdmin) {
        this.selectedRole = ADMIN_ROLES.SITE_ADMIN
        return
      }

      this.selectedRole = ADMIN_ROLES.NONE
    },

    buildRoleData(roleId) {
      const data = {}

      this.adminRoles.forEach(role => {
        if (role.id !== ADMIN_ROLES.NONE) data[role.id] = role.id === roleId
      })

      if (roleId === ADMIN_ROLES.SITE_ADMIN) {
        data.managedSites = this.selectedSiteIds
        data.managedSiteGroups = this.selectedSiteGroupIds
      } else {
        data.managedSites = []
        data.managedSiteGroups = []
      }

      return data
    },

    handleCancel() {
      if (this.isSaving) return
      this.$emit('cancel')
    },

    async handleSave() {
      const data = this.buildRoleData(this.selectedRole)

      this.isSaving = true
      try {
        await this.updateAccount({ account: this.user, data })
        this.$emit('saved', { user: this.user, data })
      } catch (error) {
        this.$emit('error', { user: this.user, data, error })
      } finally {
        this.isSaving = false
      }
    },
  },
}
</script>
